import React, { useEffect, useRef, useState } from "react";
import Modal from "react-modal";
import axios from "axios";
import ReviewForm from "./ReviewForm";
import ReviewCard from "./ReviewCard";

import "../Style/_reviews.scss";

Modal.setAppElement("#root");

const AUTO_SCROLL_DELAY = 3000; // ms entre deux avis

const ReviewContent = () => {
  const [reviews, setReviews] = useState([]);
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [feedback, setFeedback] = useState("");
  const [canScrollPrev, setCanScrollPrev] = useState(false);
  const [canScrollNext, setCanScrollNext] = useState(false);
  const [isPaused, setIsPaused] = useState(false);
  const listRef = useRef(null);

  useEffect(() => {
    // Récupérer les avis depuis l'API lors du montage du composant
    axios
      .get("https://flo.zihand.fr/db.php")
      .then((response) => {
        if (Array.isArray(response.data)) {
          setReviews(response.data);
        } else {
          console.error("La réponse de l'API n'est pas un tableau :", response.data);
        }
      })
      .catch((error) => {
        console.error("Erreur lors de la récupération des avis :", error);
      });
  }, []);

  const addReview = (newReview) => {
    setFeedback("");
    axios
      .post("https://flo.zihand.fr/db.php", newReview)
      .then((response) => {
        if (response.data.message === "Review added successfully") {
          // L'avis n'apparaît qu'après validation
          setFeedback("Merci ! Votre avis sera publié après validation.");
          setIsModalOpen(false); // Ferme le modal après l'ajout d'un avis
        } else {
          setFeedback("Une erreur est survenue, merci de réessayer.");
        }
      })
      .catch((error) => {
        console.error("Failed to add review :", error);
        if (error.response?.status === 429) {
          setFeedback("Vous avez déjà laissé un avis aujourd'hui, merci de réessayer demain.");
        } else if (error.response?.status === 403) {
          setFeedback("La vérification anti-robot a échoué, merci de réessayer.");
        } else {
          setFeedback("Une erreur est survenue, merci de réessayer.");
        }
      });
  };

  // -----------------------------------------------------
  // Carrousel : flèches actives seulement s'il reste des avis à voir
  // -----------------------------------------------------
  const updateArrows = () => {
    const list = listRef.current;
    if (!list) return;
    setCanScrollPrev(list.scrollLeft > 0);
    setCanScrollNext(list.scrollLeft + list.clientWidth < list.scrollWidth - 1);
  };

  useEffect(() => {
    updateArrows();
    window.addEventListener("resize", updateArrows);
    return () => window.removeEventListener("resize", updateArrows);
  }, [reviews]);

  const scrollReviews = (direction) => {
    const list = listRef.current;
    const card = list?.querySelector(".review-card");
    if (!card) return;
    // Avance d'une carte (largeur + espacement)
    const gap = parseFloat(getComputedStyle(list).columnGap) || 0;
    list.scrollBy({ left: direction * (card.offsetWidth + gap), behavior: "smooth" });
  };

  // -----------------------------------------------------
  // Défilement automatique : en pause au survol, au toucher, au clavier
  // ou formulaire ouvert ; désactivé si l'utilisateur limite les animations
  // -----------------------------------------------------
  useEffect(() => {
    if (isPaused || isModalOpen || reviews.length < 2) return;
    if (window.matchMedia?.("(prefers-reduced-motion: reduce)")?.matches) return;

    const timer = setInterval(() => {
      const list = listRef.current;
      if (!list) return;
      // Arrivé au bout : retour au premier avis
      if (list.scrollLeft + list.clientWidth >= list.scrollWidth - 1) {
        list.scrollTo({ left: 0, behavior: "smooth" });
      } else {
        scrollReviews(1);
      }
    }, AUTO_SCROLL_DELAY);
    return () => clearInterval(timer);
  }, [isPaused, isModalOpen, reviews]);

  const toggleModal = () => {
    setFeedback("");
    setIsModalOpen((prevIsModalOpen) => !prevIsModalOpen);
  };

  return (
    <>
      <div className="reviews_container section">
        <h3>Vos Avis</h3>

        {reviews.length > 0 && (
          <div
            className="review_carousel"
            onMouseEnter={() => setIsPaused(true)}
            onMouseLeave={() => setIsPaused(false)}
            onFocus={() => setIsPaused(true)}
            onBlur={() => setIsPaused(false)}
            onTouchStart={() => setIsPaused(true)}
            onTouchEnd={() => setIsPaused(false)}
          >
            <button type="button" className="review_arrow" onClick={() => scrollReviews(-1)} disabled={!canScrollPrev} aria-label="Avis précédents">
              ‹
            </button>
            <div className="review_list" ref={listRef} onScroll={updateArrows}>
              {reviews.map((review) => (
                <ReviewCard key={review.id} name={review.name} review={review.comment} />
              ))}
            </div>
            <button type="button" className="review_arrow" onClick={() => scrollReviews(1)} disabled={!canScrollNext} aria-label="Avis suivants">
              ›
            </button>
          </div>
        )}

        <button onClick={toggleModal} className="review_add">
          Laisser un avis
        </button>
        {!isModalOpen && feedback && <p className="review_feedback">{feedback}</p>}
        <Modal isOpen={isModalOpen} onRequestClose={toggleModal} contentLabel="Laisser un avis" className="Modal" overlayClassName="Overlay">
          <button onClick={toggleModal} className="close-button" aria-label="Fermer">
            X
          </button>
          <ReviewForm addReview={addReview} />
          {feedback && <p className="review_feedback">{feedback}</p>}
        </Modal>
      </div>
    </>
  );
};

export default ReviewContent;
